import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { toast } from 'react-toastify';

import {
  TextField as Input,
  FormHelperText,
  IconButton,
  CircularProgress,
  FormControl,
} from '@material-ui/core';
import { Search } from '@material-ui/icons';
import { useField } from '@unform/core';
import { isArray, isUndefined } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';

import { Modal } from '~/components';
import { Hidden } from '~/components/Form';
import { t } from '~/helpers';
import api from '~/services/api';

import { SearchTable } from './component';
import * as S from './styles';

export default function PesquisaTabela({
  name,
  url,
  primary,
  secondary,
  getValue,
  query,
  params: extraParams,
  header,
  nowrap,
  maxWidth,
  maxHeight,
  disabled,
  amaggi,
  filterByUser,
  aguarda,
  registerSecondary,
  ...rest
}) {
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [otherError, setOtherError] = useState('');

  const inputRef = useRef(null);
  const inputSecondRef = useRef(null);
  const modalRef = useRef(null);

  const cells = useMemo(() => header, [header]);

  const {
    fieldName,
    defaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);

  const handlePrimaryFocus = useCallback(() => {
    clearError();
    setOtherError('');
  }, []);

  const handleGetFindItem = useCallback(
    async (searchParams) => {
      try {
        setLoading(true);

        const { data } = await api.get(url, {
          params: {
            ...extraParams,
            ...searchParams,
            pesquisalista: true,
            filteruser: filterByUser,
          },
          paramsSerializer: (param) => qs.stringify(param),
        });

        if (amaggi && data.items.length > 1) {
          setDescription('');
          getValue('');
          return;
        }

        setDescription(data.items[0][secondary]);
        getValue(data.items[0]);
      } catch (err) {
        setDescription('');
        setOtherError(t('s.geral.nadaencontrado'));

        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      } finally {
        setLoading(false);
      }
    },
    [url, secondary, extraParams, filterByUser]
  );

  const handleChangeValueField = useCallback(
    (value) => {
      console.log(value);
      inputRef.current.value = value[primary];
      inputSecondRef.current.value = value[secondary];

      setDescription(value[secondary]);
      getValue(value);
    },
    [primary, secondary]
  );

  const handleKeyPress = useCallback(
    (key, value) => {
      setDescription('');

      if (key === 'Enter' && !!value)
        handleGetFindItem({
          wherefield: [primary],
          valuewherefield: [value],
          typewherefield: ['EQUALS'],
        });
    },
    [primary]
  );

  const handleOnBlur = useCallback(
    (value) => {
      setDescription('');

      if (value) {
        handleGetFindItem({
          wherefield: [primary],
          valuewherefield: [value],
          typewherefield: ['EQUALS'],
        });
      }
    },
    [primary]
  );

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
      setValue: (ref, value) => {
        ref.value = value;
        handleOnBlur(value);
      },
      clearValue: (ref) => {
        ref.value = '';
        setDescription('');
      },
    });
  }, [registerField, fieldName]);

  useEffect(() => {
    if (!aguarda) {
      if (!isUndefined(defaultValue))
        handleGetFindItem({
          wherefield: [primary],
          valuewherefield: [defaultValue],
          typewherefield: ['EQUALS'],
        });
    }
  }, [defaultValue, primary]);

  return (
    <>
      <S.Wrapper error={!!error || !!otherError}>
        <S.GroupInput error={!!error || !!otherError}>
          <Input
            inputRef={inputRef}
            defaultValue={defaultValue}
            onFocus={handlePrimaryFocus}
            disabled={loading || disabled}
            variant="outlined"
            size="small"
            className="input"
            InputLabelProps={{ shrink: true }}
            onKeyPress={({ key, target }) => handleKeyPress(key, target.value)}
            onBlur={({ target }) => handleOnBlur(target.value)}
            {...rest}
          />

          <Input
            inputRef={inputSecondRef}
            fullWidth
            disabled
            variant="outlined"
            size="small"
            className="input_disabled"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                </>
              ),
            }}
            value={description}
          />
          <IconButton
            size="small"
            type="button"
            onClick={() => modalRef.current.handleOpenModal()}
            disabled={loading || disabled}
          >
            <Search />
          </IconButton>
        </S.GroupInput>
        {registerSecondary && (
          <Hidden name={secondary} key={secondary} value={description} />
        )}
        <FormHelperText>{error || otherError}</FormHelperText>
      </S.Wrapper>

      <Modal
        form
        noPadding
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth={maxWidth}
        ref={modalRef}
        title={rest.label}
        fullScreen={window.innerWidth <= 425}
        component={(rest) => (
          <SearchTable
            nowrap={nowrap}
            url={url}
            header={cells}
            query={query}
            params={extraParams}
            maxHeight={maxHeight}
            changeField={handleChangeValueField}
            {...rest}
          />
        )}
      />
    </>
  );
}

PesquisaTabela.propTypes = {
  name: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  primary: PropTypes.string.isRequired,
  secondary: PropTypes.string.isRequired,
  getValue: PropTypes.func,
  query: PropTypes.objectOf(
    PropTypes.shape({
      wherefield: PropTypes.arrayOf(PropTypes.string).isRequired,
      valuewherefield: PropTypes.arrayOf(PropTypes.string).isRequired,
      typewherefield: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ),
  params: PropTypes.objectOf(PropTypes.any),
  nowrap: PropTypes.bool,
  header: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      formatted: PropTypes.func,
      align: PropTypes.string,
      filter: PropTypes.shape({
        type: PropTypes.string.isRequired,
        defaultValue: PropTypes.string,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
          })
        ),
        autocomplete: PropTypes.shape({
          label: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
          unique: PropTypes.string.isRequired,
        }),
      }),
    })
  ).isRequired,
  maxWidth: PropTypes.string,
  maxHeight: PropTypes.string,
  disabled: PropTypes.bool,
  amaggi: PropTypes.bool,
  userSelectFilterCountMaisQueUm: PropTypes.bool,
  filterByUser: PropTypes.bool,
  aguarda: PropTypes.bool,
  registerSecondary: PropTypes.bool,
};

PesquisaTabela.defaultProps = {
  nowrap: false,
  query: {
    wherefield: [],
    valuewherefield: [],
    typewherefield: [],
  },
  params: {},
  getValue: () => undefined,
  maxWidth: 'md',
  maxHeight: '',
  disabled: false,
  amaggi: false,
  userSelectFilterCountMaisQueUm: false,
  filterByUser: true,
  aguarda: false,
  registerSecondary: true,
};
